/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { WineClubDetails } from '../../../modules/smwe-order-details/components/wine-club-details';
import { IOrderDetailsViewProps } from '../../../modules/smwe-order-details/smwe-order-details';

export const OrderDetailsOrderInfomation: React.FC<IOrderInformation & { isWineClubOrder?: boolean; props?: IOrderDetailsViewProps }> = ({
    orderInformationProps,
    salesId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    isWineClubOrder,
    props
}) => (
    <Node {...orderInformationProps}>
        <div>
            {channelName}
        </div>
        <div>
            {channelAddress}
        </div>
        <div>
            {channelReferenceId}
        </div>
        <div>
            {salesId}
        </div>
        {/* The breakpoint here will be lg, otherwise it will cause issues in tablet landscape view */}
        <div className='d-flex float-lg-right'>
            <div className='order-1'>
                {createdDate}
            </div>
            <div className='order-2'>
                {count}
            </div>
            <div className='order-3'>
                {amount}
            </div>
        </div>
        {isWineClubOrder && props && WineClubDetailsRender(props)}
    </Node>
);

export const OrderDetailsSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
    </Node>
);

export const OrderDetailsGroupDelivery: React.FC<IGroupDelivery & { isWineClubOrder?: boolean }> = ({ deliveryProps, heading, count, address, trackingInfo, isWineClubOrder }) => {
    return (
        <Node {...deliveryProps}>
            {heading}
            {count}
            {address}
            {trackingInfo}
        </Node>
    );
};

export const OrderDetailsGroup: React.FC<IGroup & { isWineClubOrder?: boolean }> = ({ groupProps, delivery, salesLinesProps, salesLines, isWineClubOrder }) => (
    <Node {...groupProps}>
        {delivery && <OrderDetailsGroupDelivery {...delivery} isWineClubOrder={isWineClubOrder} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                        <OrderDetailsSalesLine {...salesLine} />
                        {salesLine.salesStatus}
                    </React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderDetailsGroups: React.FC<IGroups & { isWineClubOrder?: boolean; isWineClubPickup?: boolean }> = ({ groupsProps, groups, isWineClubOrder, isWineClubPickup }) => (
    <Node {...groupsProps} className={classnames(groupsProps.className, { 'ms-order-details__wine-club-group': isWineClubOrder }, { 'ms-order-details__wine-club-pickup': isWineClubPickup })}>
        {groups.map((group, index) => (
            <OrderDetailsGroup key={index} {...group} isWineClubOrder={isWineClubOrder} />
        ))}
    </Node>
);

export const OrderDetailsOrderSummary: React.FC<IOrderSummary> = ({ orderSummaryProps, heading, subtotal, shipping, tax, totalAmount, earnedPoints }) => (
    <Node {...orderSummaryProps}>
        <div>
            {heading}
        </div>
        <div>
            {subtotal}
        </div>
        <div>
            {shipping}
        </div>
        <div>
            {tax}
        </div>
        <div>
            {totalAmount}
        </div>
        <div>
            {earnedPoints}
        </div>
    </Node>
);

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderDetailsHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

export const WineClubDetailsRender = (props: IOrderDetailsViewProps) => {
    return <WineClubDetails orderDetailProps={props} wineClubData={props.wineClubData!} />;
};

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = (props) => {
    const {
        moduleProps,
        heading,
        alert,
        loading,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        isWineClubOrder,
        isWineClubPickup
    } = props;

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {groups && <OrderDetailsGroups {...groups} isWineClubOrder={isWineClubOrder} isWineClubPickup={isWineClubPickup} />}
            {orderInfomation && <OrderDetailsOrderInfomation {...orderInfomation} isWineClubOrder={isWineClubOrder} props={props} />}
            {orderSummary && <OrderDetailsOrderSummary {...orderSummary} />}
            {payment && <OrderDetailsPayment {...payment} />}
            {help && <OrderDetailsHelp {...help} />}
        </Module>
    );
};

export default OrderDetailsView;
